import React from 'react';
import { User, FileText } from 'lucide-react';
import Modal from '../UI/Modal';
import { EventHistoryEntry } from '../../types/event';
import { GuestEventHistoryEntry } from '../../services/analyticsService';
import { useAppSelector } from '../../store/hooks';

interface HistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  history: (EventHistoryEntry | GuestEventHistoryEntry)[];
  loading?: boolean;
}

// Type guard to check if entry is EventHistoryEntry
const isEventHistoryEntry = (entry: EventHistoryEntry | GuestEventHistoryEntry): entry is EventHistoryEntry => {
  return 'changed_by' in entry && (typeof entry.changed_by === 'object' || entry.changed_by === undefined);
};

const HistoryModal: React.FC<HistoryModalProps> = ({
  isOpen,
  onClose,
  title,
  history,
  loading = false
}) => {
  const { theme } = useAppSelector((state) => state.ui);

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getChangeTypeLabel = (changeType: string) => {
    const labels: Record<string, string> = {
      'created': 'Created',
      'updated': 'Updated',
      'status_change': 'Status Changed',
      'deleted': 'Deleted',
      'field_update': 'Field Updated',
      'guest_added': 'Guest Added',
      'guest_removed': 'Guest Removed',
      'rescheduled': 'Rescheduled',
      'approved': 'Approved',
      'rejected': 'Rejected',
      'companion_update': 'Companion Updated',
      'attendance_update': 'Attendance Updated',
      'response_update': 'Response Updated',
      'invitation_sent': 'Invitation Sent',
      'whatsapp_update': 'WhatsApp Updated'
    };
    return labels[changeType] || changeType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getChangeTypeColor = (changeType: string) => {
    if (theme === 'dark') {
      const colors: Record<string, string> = {
        'created': 'bg-green-900/30 text-green-300 border-green-700/50',
        'updated': 'bg-blue-900/30 text-blue-300 border-blue-700/50',
        'status_change': 'bg-purple-900/30 text-purple-300 border-purple-700/50',
        'deleted': 'bg-red-900/30 text-red-300 border-red-700/50',
        'rescheduled': 'bg-yellow-900/30 text-yellow-300 border-yellow-700/50',
        'approved': 'bg-green-900/30 text-green-300 border-green-700/50',
        'rejected': 'bg-red-900/30 text-red-300 border-red-700/50'
      };
      return colors[changeType] || 'bg-gray-800/50 text-gray-300 border-gray-700/50';
    } else {
      const colors: Record<string, string> = {
        'created': 'bg-green-100 text-green-800 border-green-200',
        'updated': 'bg-blue-100 text-blue-800 border-blue-200',
        'status_change': 'bg-purple-100 text-purple-800 border-purple-200',
        'deleted': 'bg-red-100 text-red-800 border-red-200',
        'rescheduled': 'bg-yellow-100 text-yellow-800 border-yellow-200',
        'approved': 'bg-green-100 text-green-800 border-green-200',
        'rejected': 'bg-red-100 text-red-800 border-red-200'
      };
      return colors[changeType] || 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getChangedByName = (entry: EventHistoryEntry | GuestEventHistoryEntry): string => {
    if (isEventHistoryEntry(entry)) {
      // EventHistoryEntry has changed_by as User object or undefined
      if (entry.changed_by) {
        if (entry.changed_by.first_name && entry.changed_by.last_name) {
          return `${entry.changed_by.first_name} ${entry.changed_by.last_name}`;
        }
        return entry.changed_by.email || entry.changed_by.phone || 'Unknown';
      }
    } else {
      // GuestEventHistoryEntry has changed_by as User object or null, or changed_by_name
      if (entry.changed_by) {
        if (entry.changed_by.first_name && entry.changed_by.last_name) {
          return `${entry.changed_by.first_name} ${entry.changed_by.last_name}`;
        }
        return entry.changed_by.email || entry.changed_by.phone || 'Unknown';
      }
      if (entry.changed_by_name) {
        return entry.changed_by_name;
      }
    }
    return 'System';
  };

  const getChangedByRole = (entry: EventHistoryEntry | GuestEventHistoryEntry): string => {
    if (isEventHistoryEntry(entry)) {
      if (entry.changed_by) {
        return entry.changed_by.role || 'unknown';
      }
    } else {
      // GuestEventHistoryEntry
      if (entry.changed_by) {
        return entry.changed_by.role || 'unknown';
      }
    }
    return 'system';
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Change History" size="xl">
      <div className={`${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
        {/* Subtitle */}
        <p className={`text-sm mb-6 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
          {title}
        </p>

        {/* History List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className={`text-lg ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              Loading history...
            </div>
          </div>
        ) : history.length === 0 ? (
          <div className={`text-center py-12 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No history records found</p>
          </div>
        ) : (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {history.map((entry) => (
              <div
                key={entry.id}
                className={`p-4 rounded-lg border ${
                  theme === 'dark'
                    ? 'bg-gray-800/50 border-gray-700'
                    : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getChangeTypeColor(
                        entry.change_type
                      )}`}
                    >
                      {getChangeTypeLabel(entry.change_type)}
                    </span>
                    {entry.field_name && (
                      <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        Field: {entry.field_name}
                      </span>
                    )}
                  </div>
                  <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    {formatDateTime(entry.changed_at)}
                  </div>
                </div>

                {(entry.old_value !== null && entry.old_value !== undefined) ||
                (entry.new_value !== null && entry.new_value !== undefined) ? (
                  <div className="mt-3 flex items-center gap-2 text-sm">
                    {entry.old_value && (
                      <div className={`px-2 py-1 rounded ${theme === 'dark' ? 'bg-red-900/20 text-red-300' : 'bg-red-50 text-red-700'}`}>
                        <span className="text-xs font-medium">Old:</span> {entry.old_value}
                      </div>
                    )}
                    {entry.old_value && entry.new_value && (
                      <span className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}>→</span>
                    )}
                    {entry.new_value && (
                      <div className={`px-2 py-1 rounded ${theme === 'dark' ? 'bg-green-900/20 text-green-300' : 'bg-green-50 text-green-700'}`}>
                        <span className="text-xs font-medium">New:</span> {entry.new_value}
                      </div>
                    )}
                  </div>
                ) : null}

                <div className="mt-3 flex items-center gap-2 text-sm">
                  <User className={`h-4 w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} />
                  <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                    {getChangedByName(entry)}
                  </span>
                  <span className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                    ({getChangedByRole(entry)})
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default HistoryModal;

